import { CampaignType, CompositeId } from '@app/core/models/common.models';

import { AdGroup } from '@app/core/models/ad-group.models';
import { CriterionAdditionMatchType } from '@app/criterion-addition/shared/models/criterion-addition.models';
import { ProductAdSyncMode } from '@app/criterion-addition/shared/models/product-ad-sync.models';
import { RuleLocation } from '@app/criterion-addition/shared/models/rule.models';
import { SuggestionSource } from '@app/criterion-addition/shared/models/suggestions.models';

export enum DestinationTarget {
  SelectedAdgroup = 'SELECTED-ADGROUP',
  SourceAdgroup = 'SOURCE-ADGROUP',
  SingleCampaign = 'SINGLE-CAMPAIGN',
  MatchTypeCampaigns = 'MATCH-TYPE-CAMPAIGNS',
  BrandCompetitorGenericCampaigns = 'BRAND-COMPETITOR-GENERIC-CAMPAIGNS',
}

export enum DestinationType {
  AmazonCreateBrandKeywordAdgroup = 'AMAZON-CREATE-BRAND-KEYWORD-ADGROUP',
  AmazonCreateBrandKeywordCampaignAndAdgroup = 'AMAZON-CREATE-BRAND-KEYWORD-CAMPAIGN-AND-ADGROUP',
  AmazonCreateBroadKeywordAdgroup = 'AMAZON-CREATE-BROAD-KEYWORD-ADGROUP',
  AmazonCreateBroadKeywordCampaignAndAdgroup = 'AMAZON-CREATE-BROAD-KEYWORD-CAMPAIGN-AND-ADGROUP',
  AmazonCreateCompetitorKeywordAdgroup = 'AMAZON-CREATE-COMPETITOR-KEYWORD-ADGROUP',
  AmazonCreateCompetitorKeywordCampaignAndAdgroup = 'AMAZON-CREATE-COMPETITOR-KEYWORD-CAMPAIGN-AND-ADGROUP',
  AmazonCreateExactKeywordAdgroup = 'AMAZON-CREATE-EXACT-KEYWORD-ADGROUP',
  AmazonCreateExactKeywordCampaignAndAdgroup = 'AMAZON-CREATE-EXACT-KEYWORD-CAMPAIGN-AND-ADGROUP',
  AmazonCreateGenericKeywordAdgroup = 'AMAZON-CREATE-GENERIC-KEYWORD-ADGROUP',
  AmazonCreateGenericKeywordCampaignAndAdgroup = 'AMAZON-CREATE-GENERIC-KEYWORD-CAMPAIGN-AND-ADGROUP',
  AmazonCreateKeywordAdgroup = 'AMAZON-CREATE-KEYWORD-ADGROUP',
  AmazonCreatePatAdgroup = 'AMAZON-CREATE-PAT-ADGROUP',
  AmazonCreatePhraseKeywordAdgroup = 'AMAZON-CREATE-PHRASE-KEYWORD-ADGROUP',
  AmazonCreatePhraseKeywordCampaignAndAdgroup = 'AMAZON-CREATE-PHRASE-KEYWORD-CAMPAIGN-AND-ADGROUP',
  SelectedAdgroup = 'SELECTED-ADGROUP',
  SourceAdgroup = 'SOURCE-ADGROUP',
  WalmartCreateKeywordAdgroup = 'WALMART-CREATE-KEYWORD-ADGROUP',
  WalmartCreateExactKeywordAdgroup = 'WALMART-CREATE-EXACT-KEYWORD-ADGROUP',
  WalmartCreateBroadKeywordAdgroup = 'WALMART-CREATE-BROAD-KEYWORD-ADGROUP',
  WalmartCreateExactKeywordCampaignAndAdgroup = 'WALMART-CREATE-EXACT-KEYWORD-CAMPAIGN-AND-ADGROUP',
  WalmartCreatePhraseKeywordCampaignAndAdgroup = 'WALMART-CREATE-PHRASE-KEYWORD-CAMPAIGN-AND-ADGROUP',
  WalmartCreateBroadKeywordCampaignAndAdgroup = 'WALMART-CREATE-BROAD-KEYWORD-CAMPAIGN-AND-ADGROUP',
  WalmartCreateBrandKeywordAdgroup = 'WALMART-CREATE-BRAND-KEYWORD-ADGROUP',
  WalmartCreateGenericKeywordAdgroup = 'WALMART-CREATE-GENERIC-KEYWORD-ADGROUP',
  WalmartCreateCompetitorKeywordAdgroup = 'WALMART-CREATE-COMPETITOR-KEYWORD-ADGROUP',
  WalmartCreateBrandKeywordCampaignAndAdgroup = 'WALMART-CREATE-BRAND-KEYWORD-CAMPAIGN-AND-ADGROUP',
  WalmartCreateGenericKeywordCampaignAndAdgroup = 'WALMART-CREATE-GENERIC-KEYWORD-CAMPAIGN-AND-ADGROUP',
}

export enum DestinationWarningType {
  AccountTooManyAdgroups = 'ACCOUNT-TOO-MANY-ADGROUPS',
  AdgroupDeleted = 'ADGROUP-DELETED',
  AdgroupDoesNotExist = 'ADGROUP-DOES-NOT-EXIST',
  AdgroupPaused = 'ADGROUP-PAUSED',
  AdgroupTooManyProductAds = 'ADGROUP-TOO-MANY-PRODUCT-ADS',
  AdgroupTypeNotAllowed = 'ADGROUP-TYPE-NOT-ALLOWED',
  BiddableCriterionNotAllowedForCampaignType = 'BIDDABLE-CRITERION-NOT-ALLOWED-FOR-CAMPAIGN-TYPE',
  BiddableCriterionNotAllowedInExclusion = 'BIDDABLE-CRITERION-NOT-ALLOWED-IN-EXCLUSION',
  CampaignDeleted = 'CAMPAIGN-DELETED',
  CampaignNotOptimized = 'CAMPAIGN-NOT-OPTIMIZED',
  CampaignPaused = 'CAMPAIGN-PAUSED',
  CampaignTooManyAdgroups = 'CAMPAIGN-TOO-MANY-ADGROUPS',
  CampaignTypeNotAllowed = 'CAMPAIGN-TYPE-NOT-ALLOWED',
  CriterionExists = 'CRITERION-EXISTS',
  CriterionExistsAccount = 'CRITERION-EXISTS-ACCOUNT',
  CriterionExistsBiddable = 'CRITERION-EXISTS-BIDDABLE',
  CriterionExistsNegative = 'CRITERION-EXISTS-NEGATIVE',
  CriterionLimitHard = 'CRITERION-LIMIT-HARD',
  CriterionLimitSoft = 'CRITERION-LIMIT-SOFT',
  DestinationTypeNotAllowed = 'DESTINATION-TYPE-NOT-ALLOWED',
  MatchTypeMissing = 'MATCH-TYPE-MISSING',
  MatchTypeNotAllowedForCriterionType = 'MATCH-TYPE-NOT-ALLOWED-FOR-CRITERION-TYPE',
  MatchTypeNotAllowedForDestination = 'MATCH-TYPE-NOT-ALLOWED-FOR-DESTINATION',
  MatchTypeNotAllowedTooManyWords = 'MATCH-TYPE-NOT-ALLOWED-TOO-MANY-WORDS',
  MatchTypeMandatoryForDestination = 'MATCH-TYPE-MANDATORY-FOR-DESTINATION',
  NegativeCriterionNotAllowed = 'NEGATIVE-CRITERION-NOT-ALLOWED',
  NegativeWithPositiveCriterionNotAllowed = 'NEGATIVE-WITH-POSITIVE-CRITERION-NOT-ALLOWED',
}

export interface DestinationWarning {
  warningType: DestinationWarningType;
  context?: unknown;
  critical: boolean;
}

export interface DestinationOption {
  matchType: CriterionAdditionMatchType;
  negative: boolean;
  warnings: DestinationWarning[];

  // non api
  selected?: boolean;
  disabled?: boolean;
}

export interface Destination {
  allowed: boolean;
  destinationType: DestinationType;
  destinationOptions: DestinationOption[];

  // non api
  selected?: boolean;
}

export interface DestinationGroup {
  target: DestinationTarget;
  destinations: Destination[];
}

export interface DestinationMatchType {
  id: string;
  sortId: string;
  instances: DestinationInstance[];
  destinationType?: DestinationType;
  destination: Destination | DestinationAdGroup;

  // non api
  radioValue?: boolean;
  // non api
  warnings?: DestinationWarning[];
}

export interface EffectiveDestination {
  campaignId: number;
  campaignName: string;
  campaignType: CampaignType;
  adgroupId: number;
  adgroupName: string;
  adspertAdgroupId: CompositeId;
  productAdSyncMode: ProductAdSyncMode | null;
  amzProductAdsMatchRatio: number | null;
}

export interface DestinationInstance {
  destinationId: number;
  destinationType: DestinationType;
  matchType: CriterionAdditionMatchType | null;
  negative: boolean;
  effectiveDestination: EffectiveDestination | null;
  adspertAdgroupId: CompositeId | null;
  selectedAdgroupId: CompositeId | null;
  warnings?: DestinationWarning[];
}

export interface DestinationSource<T extends DestinationInstance = DestinationInstance> {
  ruleTypeId?: number;
  proposalId?: number;
  ruleId?: number;
  createdAt?: Date;
  definedAt?: RuleLocation;
  destinations: T[];
  source?: SuggestionSource;
}

export interface DestinationAdGroup extends EffectiveDestination, AdGroup {
  destinationOptions: DestinationOption[];
  destinationEnabled?: boolean;
  isLimitedSelection?: boolean;

  // non api
  selected?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}
